<template>
  <div class="logo-container" v-if="isSpinnerShowing">
    <b-spinner class="spinner"></b-spinner>
    <b-img :src="imageURL" rounded thumbnail fluid></b-img>
  </div>
  <div v-if="appError" class="app-error-overlay">
    <div class="app-error-card">
      <h3><i class="fa fa-exclamation-triangle me-2"></i>Something went wrong</h3>
      <p>The application encountered an unexpected error. Please try refreshing the page.</p>
      <b-button class="primaryBg" @click="refreshPage">Refresh Page</b-button>
    </div>
  </div>
  <b-app v-else>
    <sidenav v-if="showSidebar" />
    <div :class="{ 'home-section': showSidebar }">
      <router-view />
    </div>
    <downloadProgressOverlay />
  </b-app>
</template>

<script setup>
import { useEventBus } from '@/composables/useEventBus';
import lazy from '@/services/lazy';
import { useToast } from 'bootstrap-vue-next';
import { computed, onErrorCaptured, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const sidenav = lazy(() => import('@/components/shared/sidenav.vue'));

const downloadProgressOverlay = lazy(
  () => import('@/components/modules/airmap/downloadProgressOverlay.vue')
);

const store = useStore();
const route = useRoute();
const { create } = useToast();
const { on: eventBusOn } = useEventBus();

const showSidebar = computed(() => {
  return route.meta.showSidebar === true;
});

const appError = ref(null);

onErrorCaptured((err, info) => {
  if (process.env.NODE_ENV === 'development') {
    console.error('Captured App Error:', err, info);
  }
  appError.value = err;
  return false;
});

const refreshPage = () => window.location.reload();

const handleToast = payload => {
  const { title, message, body, variant = 'info', delay = 2000 } = payload;
  create({
    title,
    body: message || body,
    autoHideDelay: delay,
    noHoverPause: true,
    variant,
    toastClass: `my-toaster-${variant}`,
  });
};

const isSpinnerShowing = computed(() => store.state.isSpinnerShowing);
const imageURL = computed(() => store.state.userStore.loggedInUser?.sidebar_logo);

onMounted(() => {
  eventBusOn('toast:show', handleToast);
  eventBusOn('showToast', handleToast);
});
</script>

<style scoped>
.app-error-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--dark);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.app-error-card {
  background: var(--white);
  padding: var(--spacing-lg);
  border-radius: var(--border-radius-lg);
  text-align: center;
  box-shadow: var(--shadow-xl);
  max-width: 400px;
}
.app-error-card h3 {
  color: var(--danger);
  margin-bottom: var(--spacing-md);
}
</style>
